/**
 * Component: Wealthica2GoldContainer
 *
 * Orchestrates Wealthica v2 UI: connection state, account/position loading,
 * persistence, and panels (institutions, accounts, positions). Uses cache-first
 * hooks and multi-store pattern for performance and isolation.
 *
 * Responsibilities:
 * - Display connection states and actions
 * - Load accounts/positions via cache-aware hooks
 * - Coordinate persistence and disconnect flows
 * - Render instrument detail drawer and positions panel
 */
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useRenderLoopGuard } from '@/hooks/useRenderLoopGuard';
import { useWhyRerender } from '@/hooks/useWhyRerender';
import { useAuthStore } from '@/stores/authStore';
import { ConnectionStates } from './ui/ConnectionStates';
import { useWealthica2Accounts } from '../hooks/useWealthica2Accounts';
import { useWealthica2Status } from '../hooks/useWealthica2Status';
import { logger } from '@/services/LoggingService';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { useWealthicaPositions } from '../hooks/useWealthicaPositions';
import { useAccountBalances } from '../hooks/useAccountBalances';
import { useWealthicaPersistence } from '../hooks/useWealthicaPersistence';
import { useDynamicTimestamp } from '@/hooks/useDynamicTimestamp';
import { useWealthicaDisconnect } from '../hooks/useWealthicaDisconnect';
import { PositionsPanel } from './gold/PositionsPanel';
import type { AccountWithPositions } from './ui/container-utils';
import { InstrumentDetailDrawer } from '@/components/instruments/InstrumentDetailDrawer';
import { useNavigate } from 'react-router-dom';
import { useWealthicaFlowToastBridge } from '@/providers/wealthica2/hooks/useWealthicaFlowToastBridge';
import { useWealthicaFlowProgress } from '@/providers/wealthica2/hooks/useWealthicaFlowProgress';
import { useInitialWealthicaFetchGuard } from '@/providers/wealthica2/hooks/useInitialWealthicaFetchGuard';
import { useWealthicaOpenPosition } from '@/providers/wealthica2/hooks/useWealthicaOpenPosition';
import { useWealthicaAccountsMemo } from '@/providers/wealthica2/hooks/useWealthicaAccountsMemo';

interface Wealthica2ContainerProps {
  onPositionsLoaded?: (accountsWithPositions: AccountWithPositions[], wealthicaAccounts: any[]) => void;
}

/**
 * Main container component for Wealthica integration
 * Handles account data, positions, and rendering of UI components
 * 
 * @param {object} props - Component props
 * @param {function} [props.onPositionsLoaded] - Callback when positions are loaded
 * @returns {JSX.Element} Rendered component
 */
export function Wealthica2GoldContainer({ onPositionsLoaded }: Wealthica2ContainerProps): JSX.Element {

  // Log initialization only once to avoid noisy logs during re-renders
  useEffect(() => {
    logger.info('Wealthica2GoldContainer', '🏗️ Container initialization', {
      storeMode: 'multi-store'
    });
  }, []);

  // Detect rapid re-renders to trace React #185
  useRenderLoopGuard('Wealthica2GoldContainer', 20, 200);

  const { user, isInitializing: isAuthLoading } = useAuthStore();
  const navigate = useNavigate();
  
  // Connections will be derived after store initialization
  
  // Get connection status (for backwards compatibility)
  const {
    isWealthicaConnected,
    institutionId: statusInstitutionId,
    hasToken
  } = useWealthica2Status();
  
  // 🚀 Store: Multi-store only
  const multiStore = useMultiWealthica2Store();
  // Subscribe to minimal primitives to keep memo stable and avoid use-before-declaration
  const connectionsSize = useMultiWealthica2Store(s => s.connections.size);
  const isInitializingStore = useMultiWealthica2Store(s => s.isInitializing);
  const hydrationCompletedStore = useMultiWealthica2Store(s => s.hydrationCompleted);
  const getAllConnections = useMultiWealthica2Store(s => s.getAllConnections);
  // Get ALL Wealthica connections for multi-institution display (multi-store only)
  const storeConnections = useMemo(() => getAllConnections(), [getAllConnections, connectionsSize]);
  const hasConnections = storeConnections.length > 0;
  // Treat connections as loading while store is initializing or hydration hasn't completed
  const connectionsLoading = isInitializingStore || !hydrationCompletedStore;
  
  // Get persistence preference from multi-store
  const persistDataPreference = multiStore.persistDataPreference;
  const setPersistDataPreference = multiStore.setPersistDataPreference;
  
  // Extract data from multi-store only
  // Memoize accounts to stabilize reference and reduce re-renders in consumers
  const wealthicaAccounts = useWealthicaAccountsMemo(multiStore.getAllAccounts() || []);
  
  const isWealthicaLoading = multiStore.isAnyLoadingAccounts();
  // Bridge cross-page flow toast handling (rehydration + events)
  useWealthicaFlowToastBridge(wealthicaAccounts?.length || 0);
  
  // Treat loading as a refetch when we already have accounts displayed
  const hasLoadedAccounts = (wealthicaAccounts?.length || 0) > 0;
  const isWealthicaRefetching = hasLoadedAccounts && !!isWealthicaLoading;
    
  const wealthicaError = multiStore.initializationError;
    
  const lastSyncAt = multiStore.getLastAccountsFetch(); // Get most recent fetch across all connections
    
  const fetchAccounts = multiStore.fetchAccountsForAllConnections;

  // Create stable refs for functions to prevent infinite loops
  const fetchAccountsRef = useRef(fetchAccounts);
  fetchAccountsRef.current = multiStore.fetchAccountsForAllConnections;

  // One-time fallback to directly fetch accounts and prime the store
  const fallbackAttemptedRef = useRef(false);
  // Guard to ensure initial fetch only runs once per session to prevent update-depth loops
  const initialFetchDoneRef = useRef(false);

  const { 
    institutionId: connectedInstitutionId 
  } = useWealthica2Accounts({
    persist: false // Read-only for institution ID
  }) || {};

  // Multi-store only: persistence is handled by store actions; no single connectionData needed
  const connectionData = null;

  // Use optimized custom hooks
  const { 
    accountsWithPositions,
    organizedPositionsMap,
    expandedAccounts,
    loadingPositionsFor,
    isLoadingPositions,
    loadAllPositionsForAccounts,
    loadPositionsForAccount,
    setExpandedAccountsList,
    hasLoadedInitialPositions
  } = useWealthicaPositions({
    wealthicaAccounts: wealthicaAccounts || [],
    connectedInstitutionId,
    persistDataPreference
  });

  // Cross-page flow toast handled via useWealthicaFlowToastBridge hook
  useWealthicaFlowProgress(wealthicaAccounts?.length || 0, isLoadingPositions, persistDataPreference);

  // Drawer UI state for viewing a connected position
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [positionSummary, setPositionSummary] = useState<any | null>(null);

  const handlePositionClickOpen = useWealthicaOpenPosition(user?.id, setPositionSummary, setShowDetailDrawer);

  // Instrument why rerender
  useWhyRerender('Wealthica2GoldContainer', {
    wealthicaAccountsCount: wealthicaAccounts?.length || 0,
    isWealthicaLoading,
    hasConnections,
    connectionsCount: storeConnections.length || 0,
    accountsWithPositionsCount: accountsWithPositions?.length || 0,
    isLoadingPositions,
    lastSyncAt: lastSyncAt ? new Date(lastSyncAt).toISOString() : null
  });

  // Use balance calculation hook
  const { institutionGroups } = useAccountBalances({
    wealthicaAccounts: wealthicaAccounts || [],
    accountsWithPositions,
    loadingPositionsFor
  });

  // Use optimized disconnect hook
  const disconnectMutation = useWealthicaDisconnect();

  // Remove global reconnect - will be handled per institution
  const { mutate: disconnectAccount } = disconnectMutation;

  useInitialWealthicaFetchGuard(
    !!isWealthicaConnected,
    storeConnections,
    multiStore,
    () => fetchAccountsRef.current(true),
    initialFetchDoneRef
  );
  
  // Call the callback whenever positions are loaded
  useEffect(() => {
    if (onPositionsLoaded && (accountsWithPositions?.length || 0) > 0 && wealthicaAccounts && (wealthicaAccounts?.length || 0) > 0) {
      logger.info('Wealthica2GoldContainer', 'Notifying parent about loaded positions', {
        accountsCount: accountsWithPositions?.length || 0,
        accountsOriginalCount: wealthicaAccounts?.length || 0,
        positionCount: (accountsWithPositions || []).reduce((total, acc) => total + (acc?.positions?.length || 0), 0),
        storeMode: 'multi-store'
      });
      onPositionsLoaded(accountsWithPositions, wealthicaAccounts);
    }
  // FIXED: Removed onPositionsLoaded from dependencies to prevent infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsWithPositions?.length, wealthicaAccounts?.length]);

  // Enhanced status display using extracted utility
  const dynamicTimestamp = useDynamicTimestamp(lastSyncAt ? new Date(lastSyncAt) : null, 60000);

  // Use extracted persistence hook
  useWealthicaPersistence({
    persistDataPreference,
    connectionData,
    wealthicaAccounts,
    accountsWithPositions,
    isLoadingPositions
  });

  // Main container render
  return (
    <>
      {/* Connection states - shown during loading, errors, or no accounts */}
      <ConnectionStates 
        isAuthLoading={isAuthLoading}
        isWealthicaLoading={isWealthicaLoading}
        isWealthicaRefetching={isWealthicaRefetching}
        wealthicaError={wealthicaError ? new Error(wealthicaError) : null}
        isWealthicaConnected={!!isWealthicaConnected}
        accountCount={wealthicaAccounts?.length || 0}
        onRetry={() => {
          fetchAccountsRef.current(true);
        }}
        lastSyncAt={lastSyncAt ? new Date(lastSyncAt) : null}
        hasConnections={hasConnections}
        tokenState="valid"
        isLoadingPositions={isLoadingPositions}
        hasLoadedAccounts={hasLoadedAccounts}
      />
      
      {/* Positions list when we have data */}
      {!isWealthicaLoading && (hasConnections || (institutionGroups?.length || 0) > 0) && (
          <PositionsPanel
          institutionGroups={institutionGroups}
          storeConnections={storeConnections}
          connectionsLoading={connectionsLoading}
          multiStore={multiStore}
          wealthicaAccountsLength={wealthicaAccounts?.length || 0}
          accountsWithPositions={accountsWithPositions || []}
          loadingPositionsFor={loadingPositionsFor}
          isLoadingPositions={isLoadingPositions}
          expandedAccounts={expandedAccounts}
          setExpandedAccountsList={setExpandedAccountsList}
          loadPositionsForAccount={loadPositionsForAccount}
          onPositionClick={handlePositionClickOpen}
          defaultLastSyncAt={lastSyncAt}
          disconnectAccount={disconnectAccount}
          persistDataPreference={persistDataPreference}
          setPersistDataPreference={setPersistDataPreference}
        />
      )}
      {/* Position Detail Drawer */}
      <InstrumentDetailDrawer
        asset={null}
        open={showDetailDrawer}
        onClose={() => setShowDetailDrawer(false)}
        mode="position"
        position={positionSummary}
      />
    </>
  );
}
